<script setup>
import { ref, computed } from 'vue';
import ANButton from '@/components/common/AnButton.vue';
import { Card } from 'primevue';

const props = defineProps({
  onSubmit: {
    type: Function,
    required: true
  },
  onBack: {
    type: Function,
    required: false
  },
  isSubmitted: {
    type: Boolean,
    default: false
  },
  isDashboardMode: {
    type: Boolean,
    default: false
  }
});

// Risk assessment data - moved from constant-services.js
const riskAssessmentData = {
  title: "Risk Profile Assessment",
  subtitle: "Answer a few questions to determine your ideal investment strategy",
  questions: [
    {
      id: "investmentTimeframe",
      question: "What is the investment time frame for this portfolio?",
      options: [
        { value: "lessThan1Year", label: "Less than 1 year", score: 1 },
        { value: "1to2Years", label: "1-2 years", score: 2 },
        { value: "3to5Years", label: "3-5 years", score: 3 },
        { value: "moreThan5Years", label: "More than 5 years", score: 4 }
      ]
    },
    {
      id: "investmentFamiliarity",
      question: "How familiar are you with investing?",
      options: [
        { value: "low", label: "Low", score: 1 },
        { value: "moderate", label: "Moderate", score: 2 },
        { value: "high", label: "High", score: 3 }
      ]
    },
    {
      id: "marketDeclineResponse",
      question: "When markets decline what do you do?",
      options: [
        { value: "sellRisky", label: "Sell risky investments", score: 1 },
        { value: "doNothing", label: "Do nothing", score: 2 },
        { value: "buyMore", label: "Buy more", score: 3 }
      ]
    },
    {
      id: "weeklyIncomeVariation",
      question: "How much portfolio decline can you tolerate in a week?",
      options: [
        { value: "none", label: "None", score: 1 },
        { value: "upTo3Percent", label: "Up to 3%", score: 2 },
        { value: "3to10Percent", label: "3% - 10%", score: 3 },
        { value: "above10Percent", label: ">10%", score: 4 }
      ]
    },
    {
      id: "equityPercentage",
      question: "What % of your portfolio would you invest in equities?",
      options: [
        { value: "upTo10", label: "Up to 10%", score: 1 },
        { value: "20to30", label: "20% - 30%", score: 2 },
        { value: "over30", label: "Over 30%", score: 3 }
      ]
    },
    {
      id: "investmentObjective",
      question: "What is your primary investment objective?",
      options: [
        { value: "capitalPreservation", label: "Capital preservation", score: 1 },
        { value: "incomeGeneration", label: "Income generation", score: 2 },
        { value: "wealthCreation", label: "Wealth creation", score: 3 }
      ]
    }
  ]
};

const questions = riskAssessmentData.questions;
const answers = ref({});

// Calculate if all questions are answered
const isFormComplete = computed(() => {
  return questions.every(q => answers.value[q.id]);
});

// Handle answer selection
const selectAnswer = (questionId, option) => {
  if (props.isSubmitted) return; // Prevent changes after submission
  answers.value[questionId] = option;
};

// Handle form submission
const handleSubmit = () => {
  if (isFormComplete.value && !props.isSubmitted) {
    props.onSubmit(answers.value);
  }
};

// Get answer class for styling
const getAnswerClass = (questionId, optionValue) => {
  const selected = answers.value[questionId]?.value === optionValue;
  return {
    'answer-option': true,
    'selected': selected,
    'disabled': props.isSubmitted
  };
};
</script>

<template>
  <div class="risk-questionnaire">
    <!-- Upfront disclaimer for landing page mode -->
    <div v-if="!isDashboardMode" class="landing-disclaimer">
      <div class="disclaimer-card">
        <i class="pi pi-info-circle disclaimer-icon"></i>
        <p class="disclaimer-text">
          <strong>Educational Purpose Only:</strong> This assessment will generate an illustrative portfolio example for educational purposes.
          Actual investment recommendations require sign-up, KYC verification, and a signed Investment Advisory Agreement as per SEBI regulations.
        </p>
      </div>
    </div>

    <div class="questionnaire-header">
      <!-- Step Progress Indicator -->
      <div class="step-progress">
        <div class="progress-steps">
          <div class="step completed">
            <div class="step-number">1</div>
            <span class="step-label">Personal Info</span>
          </div>
          <div class="step-connector completed"></div>
          <div class="step active">
            <div class="step-number">2</div>
            <span class="step-label">Risk Assessment</span>
          </div>
          <div class="step-connector"></div>
          <div class="step">
            <div class="step-number">3</div>
            <span class="step-label">Your Portfolio</span>
          </div>
        </div>
      </div>

      <h2 class="questionnaire-title">{{ riskAssessmentData.title }}</h2>
      <p class="questionnaire-subtitle">{{ riskAssessmentData.subtitle }}</p>

      <!-- Question Progress -->
      <div class="progress-indicator">
        <span class="progress-text">
          Question {{ Object.keys(answers).length }} of {{ questions.length }} completed
        </span>
        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: `${(Object.keys(answers).length / questions.length) * 100}%` }"
          ></div>
        </div>
      </div>

      <!-- Back Button -->
      <div v-if="onBack" class="navigation-buttons">
        <button @click="onBack" class="back-to-info-button">
          ← Back to Personal Info
        </button>
      </div>
    </div>

    <div class="questions-container">
      <Card
        v-for="(question, index) in questions"
        :key="question.id"
        class="question-card"
      >
        <template #header>
          <div class="question-header">
            <span class="question-number">{{ index + 1 }}</span>
            <h3 class="question-text">{{ question.question }}</h3>
          </div>
        </template>

        <template #content>
          <div class="answers-grid">
            <div
              v-for="option in question.options"
              :key="option.value"
              :class="getAnswerClass(question.id, option.value)"
              @click="selectAnswer(question.id, option)"
            >
              <span class="option-text">{{ option.label }}</span>
              <span v-if="answers[question.id]?.value === option.value" class="check-icon">✓</span>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <div class="submit-section">
      <ANButton
        :label="isSubmitted ? 'Assessment Submitted' : 'Submit Assessment'"
        @click="handleSubmit"
        :disabled="!isFormComplete || isSubmitted"
        class="submit-button"
      />
      <p v-if="!isFormComplete" class="completion-hint">
        Please answer all questions to submit your assessment
      </p>
    </div>
  </div>
</template>

<style scoped>
.risk-questionnaire {
  max-width: 900px;
  margin: 0 auto;
  padding: 0;
  font-family: "Satoshi-Variable", sans-serif;
}

.landing-disclaimer {
  margin-bottom: 32px;
}

.disclaimer-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #2196f3;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
}

.disclaimer-icon {
  flex-shrink: 0;
  font-size: 24px;
  color: #1976d2;
  margin-top: 2px;
}

.disclaimer-text {
  flex: 1;
  margin: 0;
  color: #0d47a1;
  font-size: 0.95rem;
  line-height: 1.6;
}

.disclaimer-text strong {
  color: #01579b;
  font-weight: 700;
}

.questionnaire-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 32px 20px;
  background: var(--an-white);
  border-radius: 12px;
  box-shadow: var(--an-card-shadow);
  border: 1px solid var(--an-gray3);
}

.step-progress {
  margin-bottom: 32px;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 400px;
  margin: 0 auto;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  background: var(--an-gray3);
  color: var(--an-gray2);
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  box-shadow: 0 4px 8px rgba(0, 78, 80, 0.2);
}

.step.completed .step-number {
  background: var(--an-success);
  color: var(--an-white);
}

.step-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  text-align: center;
}

.step.active .step-label {
  color: var(--an-primary);
}

.step.completed .step-label {
  color: var(--an-success);
}

.step-connector {
  width: 40px;
  height: 2px;
  background: var(--an-gray3);
  margin: 0 8px;
  transition: all 0.3s ease;
}

.step-connector.completed {
  background: var(--an-success);
}

.navigation-buttons {
  margin-top: 20px;
}

.back-to-info-button {
  padding: 10px 20px;
  background: var(--an-white);
  border: 2px solid var(--an-gray3);
  border-radius: 8px;
  color: var(--an-text-secondary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.back-to-info-button:hover {
  border-color: var(--an-primary);
  color: var(--an-primary);
  background: var(--an-background-light);
}

.questionnaire-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 50%, var(--an-tertiary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.questionnaire-subtitle {
  font-size: 1.125rem;
  color: var(--an-text-secondary);
  margin-bottom: 24px;
  line-height: 1.5;
}

.progress-indicator {
  max-width: 350px;
  margin: 0 auto;
}

.progress-text {
  display: block;
  font-size: 0.875rem;
  color: var(--an-text-secondary);
  margin-bottom: 8px;
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 10px;
  background: var(--an-gray3);
  border-radius: 5px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--an-primary), var(--an-secondary));
  transition: width 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 78, 80, 0.2);
}

.questions-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 40px;
}

.question-card {
  border: 1px solid var(--an-gray3);
  border-radius: 12px;
  overflow: hidden;
  background: var(--an-white);
  box-shadow: var(--an-card-shadow);
  transition: all 0.3s ease;
}

.question-card:hover {
  box-shadow: 0 8px 24px rgba(0, 78, 80, 0.08);
}

.question-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: var(--an-white2);
  border-bottom: 1px solid var(--an-gray3);
}

.question-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 4px 8px rgba(0, 78, 80, 0.2);
}

.question-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
  flex: 1;
  line-height: 1.4;
}

.answers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  padding: 24px;
}

.answer-option {
  padding: 18px 20px;
  border: 2px solid var(--an-gray3);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--an-white);
  font-weight: 500;
}

.answer-option:hover:not(.disabled) {
  border-color: var(--an-primary);
  background: var(--an-background-light);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.12);
}

.answer-option.selected {
  border-color: var(--an-primary);
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.25);
}

.answer-option.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--an-gray1);
}

.option-text {
  font-weight: 500;
  font-size: 0.95rem;
}

.check-icon {
  font-weight: bold;
  font-size: 1.2rem;
}

.submit-section {
  text-align: center;
  background: var(--an-white);
  padding: 32px 24px;
  border-radius: 12px;
  border: 1px solid var(--an-gray3);
  box-shadow: var(--an-card-shadow);
}

.submit-button {
  padding: 16px 48px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 10px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  border: none;
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.25);
  transition: all 0.3s ease;
  cursor: pointer;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 78, 80, 0.3);
}

.submit-button:disabled {
  background: var(--an-gray2);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.completion-hint {
  color: var(--an-text-secondary);
  font-size: 0.875rem;
  margin: 0;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .risk-questionnaire {
    padding: 16px;
  }

  .questionnaire-title {
    font-size: 1.5rem;
  }

  .answers-grid {
    grid-template-columns: 1fr;
  }

  .question-header {
    padding: 16px;
  }

  .question-text {
    font-size: 1rem;
  }
}
</style>
